import React, { useEffect, useMemo } from 'react';
import { View, Text, Pressable } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { addPostReaction, removePostReaction } from '@/data/connect';
import { LegendList } from "@legendapp/list";
import Post from '@/components/Connect/Boards/Post';
import ContentLoader from '@/components/General/ContentLoader';
import { ConnectedReactionMenu } from '@/components/Connect/ReactionMenu';
import Toggle from '@/components/General/inputs/Toggle';
import Unicon from '@/components/General/Unicon';
import { Tooltip } from '@/components/General/Tooltip';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import useMarkNotificationsRead from '@/utils/hooks/useMarkNotificationsRead';
import usePostViewTracking from '@/utils/hooks/usePostViewTracking';
import useBoardsFeedStore from '@/data/store/useBoardsFeedStore';
import { useNavigation, useIsFocused } from 'expo-router';
import PageTitle from '@/components/General/PageTitle';
import SplitView from '@/components/General/SplitView';
import BoardsList from '@/components/Connect/Boards/BoardsList';

export default function MyFeedScreen() {
    const isFocused = useIsFocused()

    const navigation = useNavigation();
    const queryClient = useQueryClient()
    const { data: loggedInUser } = Query.people.loggedInUser()
    const { data: boardsCurrentUser } = Query.boards.all();
    const showAllBoards = useBoardsFeedStore(state => state.showAllBoards);
    const toggleShowAllBoards = useBoardsFeedStore(state => state.toggleShowAllBoards);

    const canViewAllBoards = !!loggedInUser?.isAdmin || !!loggedInUser?.can?.('View All Boards');

    const messageBoards = useMemo(() => (boardsCurrentUser || []).filter(b => ['MESSAGE_BOARD', 'ANNOUNCEMENTS'].includes(b.type)), [boardsCurrentUser]);

    // Board membership: open boards (no roles) or boards where the user has a matching role.
    // Mirrors BoardsList.jsx isBoardMember logic.
    const userRoleIds = useMemo(() => new Set(
        (loggedInUser?.Roles || loggedInUser?.roles || []).map(r => r.id || r.roleID).filter(Boolean)
    ), [loggedInUser]);
    const isBoardMember = useMemo(() => (board) => {
        if (board.createdByID && board.createdByID === loggedInUser?.id) return true;
        if (!board.Roles || board.Roles.length === 0) return true;
        return board.Roles.some(r => userRoleIds.has(r.roleID));
    }, [loggedInUser, userRoleIds]);

    const boardIDs = useMemo(() => {
        //Hidden (per-user) and archived (org-wide, read-dead) boards never
        //contribute posts to My Feed.
        const feedBoards = messageBoards.filter(b => (b.type === 'ANNOUNCEMENTS' || !b.hiddenForUser) && !b.isArchived);
        if (canViewAllBoards && showAllBoards) return feedBoards.map(b => b.id);
        return feedBoards.filter(isBoardMember).map(b => b.id);
    }, [messageBoards, canViewAllBoards, showAllBoards, isBoardMember]);

    const { data: posts, isLoading: loadingPosts, isFetchingNextPage, hasNextPage, fetchNextPage } = Query.posts.myBoardsFeed({boardIDs});

    // Refetch when the showAllBoards toggle changes — same query key, new boardIDs closure.
    // Skip first run to avoid a redundant invalidate on mount.
    const isFirstRun = React.useRef(true);
    useEffect(() => {
        if (isFirstRun.current) {
            isFirstRun.current = false;
            return;
        }
        queryClient.invalidateQueries({ queryKey: QueryKeys.posts.myBoardsFeed() });
    }, [showAllBoards]);

    //Viewing My Feed = viewing all boards' posts: clear all unread board notifications.
    //(Board notifications rarely carry parameters.boardID — matcher falls back to path.)
    useMarkNotificationsRead({ types: ["NEW_POST", "NEW_COMMENT", "ORGANIZATION_ANNOUNCEMENT"] });

    const {onViewableItemsChanged, viewabilityConfig} = usePostViewTracking();

    //Shared query key with MyBoardsFeedSection — guard against cached pages
    //fetched under different boardIDs (e.g. before a board was archived).
    const boardIDSet = useMemo(() => new Set(boardIDs), [boardIDs]);
    const flatPosts = useMemo(() => (posts?.pages?.flatMap(page => page.data) || []).filter(p => boardIDSet.has(p?.boardID)), [posts, boardIDSet]);

    const updatePostInQueryCache = (postID, updater) => {
        queryClient.setQueryData(QueryKeys.posts.myBoardsFeed(), old => {
            if(!old) return old;
            return {...old, pages: old.pages.map(page => ({...page, data: page.data.map(o => o.id == postID ? updater(o) : o)}))}
        })
    }

    const addReactionMutation = useMutation({
        mutationFn: addPostReaction,
        onMutate: (data) => {
            if(!data) return;
            updatePostInQueryCache(data.postID, o => ({...o, Reactions: [...(o.Reactions || []), {...data, Person: loggedInUser}]}))
        }
    })

    const removeReactionMutation = useMutation({
        mutationFn: removePostReaction,
        onMutate: (data) => {
            if(!data) return;
            updatePostInQueryCache(data.postID, o => ({...o, Reactions: [...(o.Reactions?.filter(r => !(r.postID == data.postID && r.personID == data.personID)) || [])]}))
        }
    })

    useEffect(() => {
        navigation.setOptions({
            headerTitle: () => <PageTitle title="My Feed" subtitle={canViewAllBoards && showAllBoards ? "All Boards" : "My Boards"} />,
        });
    }, [navigation, canViewAllBoards, showAllBoards]);

    const renderItem = ({ item }) => {
        return <Post key={item.id} post={item} showBoardName />
    };

    const updateReaction = async (post, name) => {
        const currentUserReaction = post?.Reactions?.find(r => r.Person?.id == loggedInUser?.id)

        if(currentUserReaction?.reaction == name){
            await removeReactionMutation.mutateAsync({postID: post.id, personID: loggedInUser?.id})
            return
        }

        if(currentUserReaction){
            await removeReactionMutation.mutateAsync({postID: post.id, personID: loggedInUser?.id})
        }

        await addReactionMutation.mutateAsync({postID: post.id, reaction: name, personID: loggedInUser?.id})
    }

    let contentLoaders = [];

    if(loadingPosts){
        for(let i = 0; i < 5; i++){
            contentLoaders.push(<View key={"loaders" + i} style={styles.contentLoaderRow}>
                <ContentLoader containerHeight={200} borderRadius={0} staggerPosition={i}/>
            </View>)
        }
    }

    const toggleHeader = canViewAllBoards ? (
        <View style={styles.toggleBar}>
            <Pressable
                style={styles.toggleCopy}
                onPress={() => toggleShowAllBoards()}
                accessibilityRole="switch"
                accessibilityLabel="Show All Boards"
                accessibilityHint="Includes posts from boards you are not a member of"
                accessibilityState={{ checked: showAllBoards }}
                testID="boards-feed-show-all-toggle-row"
            >
                <View style={styles.toggleLabelWrap}>
                    <Text style={styles.toggleLabel}>Show All Boards</Text>
                    <Tooltip
                        title="Show All Boards"
                        subtitle="Shows posts from boards you're not a member of, since you have permission to view all boards."
                        appearOnTouch
                    >
                        <Unicon name="help-circle-outline" size={16} colorVariant="textTertiary" />
                    </Tooltip>
                </View>
                <Text style={styles.toggleDescription}>Include posts outside your board memberships</Text>
            </Pressable>
            <Toggle
                value={showAllBoards}
                onValueChange={() => toggleShowAllBoards()}
                testID="boards-feed-show-all-toggle"
            />
        </View>
    ) : null;

    return (
        <SplitView sidebar={<BoardsList selectedBoardID="__myFeed__" display="compact" />}>
        <View style={styles.feedContainer}>
            <View style={styles.listWrapper}>
                <LegendList recycleItems keyExtractor={(item) => item.id}
                    contentInsetAdjustmentBehavior="automatic"
                    contentContainerStyle={styles.listContent}
                    showsVerticalScrollIndicator={false}
                    estimatedItemSize={220}
                    data={loadingPosts ? [] : flatPosts}
                    renderItem={renderItem}
                    onViewableItemsChanged={onViewableItemsChanged}
                    viewabilityConfig={viewabilityConfig}
                    progressViewOffset={1000}
                    refreshing={false}
                    onRefresh={() => queryClient.invalidateQueries({queryKey: QueryKeys.posts.myBoardsFeed()})}
                    onEndReachedThreshold={0.5}
                    onEndReached={() => {
                        if(!isFetchingNextPage && hasNextPage) fetchNextPage()
                    }}
                    ListHeaderComponent={toggleHeader}
                    ListFooterComponent={!loadingPosts
                        ? <View style={styles.footerContainer}>{
                            isFetchingNextPage
                            ? <ContentLoader containerHeight={150} borderRadius={0} />
                            : flatPosts?.length == 0 ? <EmptyListPlaceholder icon="newspaper-outline" spacing={10} message="No posts yet!"/>
                            : <View></View>
                        }
                        </View>
                        : <View>
                            {contentLoaders}
                        </View>}
                    keyboardShouldPersistTaps="handled"
                />
            </View>

            <ConnectedReactionMenu
                noMenu
                focused={isFocused}
                onReactionPress={(post, name) => updateReaction(post, name)}
                canReply={false}
            />
        </View>
        </SplitView>
    )
}

const styles = StyleSheet.create(({ colors, spacing, typography, fonts }, rt) => ({
    contentLoaderRow: {
        flexDirection: "row",
        justifyContent: "space-between",
        marginHorizontal: rt.breakpoint === 'xs' ? 0 : 12,
        marginVertical: 8,
        overflow: 'hidden',
        borderRadius: rt.breakpoint === 'xs' ? 0 : 16,
    },
    feedContainer: {
        flex: 1,
        width: "100%",
        alignItems: "center",
        backgroundColor: 'transparent',
    },
    toggleBar: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        gap: spacing.md,
        marginHorizontal: rt.breakpoint === 'xs' ? 0 : 12,
        paddingHorizontal: rt.breakpoint === 'xs' ? 14 : 20,
        paddingTop: spacing.sm,
        paddingBottom: spacing.md,
    },
    toggleCopy: {
        flex: 1,
        gap: 2,
    },
    toggleLabelWrap: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
    },
    toggleLabel: {
        color: colors.text,
        fontSize: typography.body,
        fontFamily: fonts.bold,
    },
    toggleDescription: {
        color: colors.textTertiary,
        fontSize: typography.caption,
        fontFamily: fonts.body,
    },
    listWrapper: {
        flex: 1,
        width: "100%",
        maxWidth: 780,
    },
    listContent: {
        paddingTop: rt.breakpoint === 'xs' ? spacing.xs : spacing.sm,
    },
    footerContainer: {
        paddingBottom: 80,
    }
}))
